// Contribution from Mercatura DeFi Team to Menese Protocol
// (2-of-2 Threshold ECDSA — Tier 7 §3.F Candid IDL).
// Mercatura Forum proprietary.
//
// Hand-written Candid IDL factory matching
// `canister/menese-2of2-canister/menese-2of2-canister.did`. Drift
// between this IDL and the canister's strict-Candid export is
// detected by the §3.M T7.5 acceptance gate's strict-Candid check.

import type { IDL as IdlNS } from "@dfinity/candid";
import { IDL } from "@dfinity/candid";

export const EncryptedBlob = IDL.Record({
  aead: IDL.Vec(IDL.Nat8),
  salt: IDL.Vec(IDL.Nat8),
  version: IDL.Nat32,
});

export const RegisterUserPubkeyArgs = IDL.Record({
  user_pubkey: IDL.Vec(IDL.Nat8),
  user_cl_pubkey: IDL.Vec(IDL.Nat8),
  encrypted_blob: EncryptedBlob,
});

export const RegisterUserPubkeyReply = IDL.Variant({
  Ok: IDL.Null,
  AlreadyRegistered: IDL.Null,
  InvalidPubkey: IDL.Text,
  InvalidBlob: IDL.Text,
});

export const FetchEncryptedBlobReply = IDL.Variant({
  Ok: EncryptedBlob,
  NotRegistered: IDL.Null,
});

export const CombinedPublicKeyReply = IDL.Variant({
  Ok: IDL.Vec(IDL.Nat8),
  NotRegistered: IDL.Null,
});

export const FetchNimPresigOk = IDL.Record({
  pid: IDL.Vec(IDL.Nat8),
  canister_public: IDL.Vec(IDL.Nat8),
  canister_cl_pubkey: IDL.Vec(IDL.Nat8),
  production_cl_crs: IDL.Vec(IDL.Nat8),
});

export const FetchNimPresigReply = IDL.Variant({
  Ok: FetchNimPresigOk,
  PoolEmpty: IDL.Null,
  NotInitialized: IDL.Null,
  ProtocolError: IDL.Text,
});

export const SignViaNimOk = IDL.Record({
  signature: IDL.Vec(IDL.Nat8),
});

export const SignViaNimReply = IDL.Variant({
  Ok: SignViaNimOk,
  UnknownPresig: IDL.Null,
  ConsumedPresig: IDL.Null,
  InvalidContribution: IDL.Text,
  BadDeviceContribution: IDL.Text,
  NotRegistered: IDL.Null,
  ProtocolError: IDL.Text,
});

export const NimPoolStatusReply = IDL.Record({
  pool_depth: IDL.Nat64,
  max_pool_depth: IDL.Nat32,
  refill_threshold: IDL.Nat32,
  default_ttl_ns: IDL.Nat64,
  consumed_count: IDL.Nat64,
  oldest_expires_at_ns: IDL.Opt(IDL.Nat64),
});

export const CanisterStatusReply = IDL.Record({
  protocol_version: IDL.Nat32,
  registered_users: IDL.Nat64,
  canister_share_initialized: IDL.Bool,
});

export const idlFactory = ({}: { IDL: typeof IdlNS } | object = {}) =>
  IDL.Service({
    register_user_pubkey: IDL.Func(
      [RegisterUserPubkeyArgs],
      [RegisterUserPubkeyReply],
      [],
    ),
    update_encrypted_blob: IDL.Func(
      [EncryptedBlob],
      [RegisterUserPubkeyReply],
      [],
    ),
    fetch_encrypted_blob: IDL.Func([], [FetchEncryptedBlobReply], ["query"]),
    combined_public_key: IDL.Func([], [CombinedPublicKeyReply], ["query"]),
    canister_production_cl_crs: IDL.Func([], [IDL.Vec(IDL.Nat8)], ["query"]),
    fetch_nim_presig: IDL.Func([], [FetchNimPresigReply], []),
    sign_via_nim: IDL.Func([IDL.Vec(IDL.Nat8)], [SignViaNimReply], []),
    fill_nim_pool: IDL.Func([IDL.Nat32], [NimPoolStatusReply], []),
    nim_pool_status: IDL.Func([], [NimPoolStatusReply], ["query"]),
    canister_status: IDL.Func([], [CanisterStatusReply], ["query"]),
  });

export const init = ({}: { IDL: typeof IdlNS } | object = {}) => [];
